import { Link } from '@tanstack/react-router';
import { use, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { Market, Product, Variant } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { ChangeStockModal, useInventoryItem, useStockLocations } from '@/features/inventory';
import { useMarkets } from '@/features/markets';
import {
  ScopeChip,
  useWatchedVariantCost,
  VariantDetailField,
  VariantFieldGroups,
  VariantDetailGrid,
  VariantDetailGroup,
  VariantDetailValue,
} from '@/features/products';

import { MarketPriceDraftsContext } from '../hooks/use-market-price-drafts';
import { useMarketPrices } from '../hooks/use-market-prices';
import { StorePricingContext } from '../hooks/use-store-pricing-drafts';
import { draftFromRow, priceSource } from '../schemas/pricing-rules';

import { PriceSourceBadge } from './price-source-badge';
import { StorePriceCell } from './store-variant-cells';

function PricingHere({ variant }: { variant: Variant }) {
  const { t } = useTranslation('storeCatalog');
  const format = useFormat();
  const pricing = use(StorePricingContext);
  const row = pricing?.rowOf(variant.id);
  const f = (key: string) => t(`storeVariants.fields.${key}`);
  const draft = row && pricing ? pricing.draftFor(row) : null;
  const saved = row ? draftFromRow(row) : null;
  // The draft follows what's typed; a price being cleared falls back to what it sells at now.
  const price = draft?.price ?? (saved?.price ? null : row?.effective) ?? null;
  const cost = useWatchedVariantCost(variant.id);
  const margin =
    price && cost && price.amount > 0 && price.currency === cost.currency
      ? `${Math.round(((price.amount - cost.amount) / price.amount) * 100)}%`
      : '—';
  return (
    <VariantDetailGroup title={t('storeVariants.groups.pricing')}>
      <VariantDetailGrid>
        <VariantDetailField label={f('compareAt')}>
          <StorePriceCell variant={variant} field="compareAt" className="w-full" />
        </VariantDetailField>
        <VariantDetailValue label={f('basePrice')}>
          {row ? format.money(row.base) : '—'}
        </VariantDetailValue>
        <VariantDetailField label={f('priceSource')}>
          <span className="py-1 font-normal">
            {row ? <PriceSourceBadge source={priceSource(row)} /> : '—'}
          </span>
        </VariantDetailField>
        <VariantDetailValue label={f('marginHere')}>{margin}</VariantDetailValue>
      </VariantDetailGrid>
    </VariantDetailGroup>
  );
}

function StockHere({ storeId, variant }: { storeId: string; variant: Variant }) {
  const { t } = useTranslation(['storeCatalog', 'inventory']);
  const { can } = usePermissions();
  const readable = can('inventory.read');
  const tracked = readable && variant.trackInventory;
  const item = useInventoryItem(storeId, variant.id, tracked);
  const locations = useStockLocations(storeId);
  const [changing, setChanging] = useState(false);
  const nameOf = (id: string) =>
    locations.data?.find((location) => location.id === id)?.name ?? '—';
  return (
    <VariantDetailGroup
      title={t('storeCatalog:storeVariants.groups.stock')}
      action={
        tracked && can('inventory.write') ? (
          <Button
            type="button"
            variant="link"
            size="xs"
            className="text-primary h-auto p-0 text-[13px]"
            aria-label={t('storeCatalog:storeVariants.changeStockFor', { variant: variant.title })}
            onClick={() => setChanging(true)}
          >
            {t('storeCatalog:storeVariants.changeStock')}
          </Button>
        ) : null
      }
    >
      {!readable ? (
        <p className="text-muted-foreground text-[13px]">—</p>
      ) : !variant.trackInventory ? (
        <p className="text-muted-foreground text-[13px]">{t('inventory:stockCard.notTracked')}</p>
      ) : item.isPending ? (
        <Skeleton className="h-10" />
      ) : item.error ? (
        <ErrorState error={item.error} onRetry={() => void item.refetch()} compact />
      ) : (
        <VariantDetailGrid>
          {item.data.levels.map((level) => (
            <VariantDetailValue key={level.locationId} label={nameOf(level.locationId)}>
              {t('storeCatalog:storeVariants.availableCount', { count: level.available })}
            </VariantDetailValue>
          ))}
        </VariantDetailGrid>
      )}
      {changing ? (
        <ChangeStockModal
          storeId={storeId}
          variant={{ id: variant.id, title: variant.title }}
          open
          onOpenChange={setChanging}
        />
      ) : null}
    </VariantDetailGroup>
  );
}

function MarketPriceField({
  storeId,
  productId,
  variant,
  market,
}: {
  storeId: string;
  productId: string;
  variant: Variant;
  market: Market;
}) {
  const { t } = useTranslation('storeCatalog');
  const drafts = use(MarketPriceDraftsContext);
  const query = useMarketPrices(storeId, market.id, productId);
  const entry = query.data?.items.find((item) => item.variantId === variant.id);
  if (!drafts) return null;
  const error = drafts.errorFor(market.id, variant.id);
  const label = t('marketPrices.marketOption', { name: market.name, currency: market.currency });
  return (
    <VariantDetailField label={label}>
      {query.isPending ? (
        <Skeleton className="h-8" />
      ) : (
        <MoneyInput
          currency={market.currency}
          value={drafts.priceFor(market.id, variant.id, entry)}
          disabled={!drafts.canWrite}
          placeholder={t('storeVariants.marketAuto')}
          aria-label={t('storeVariants.marketPriceFor', {
            market: market.name,
            variant: variant.title,
          })}
          aria-invalid={Boolean(error)}
          onChange={(price) => drafts.setPrice(market.id, variant.id, price, entry)}
          className={cn(
            'h-8 pl-12 text-xs',
            drafts.isDirty(market.id, variant.id) && 'border-primary bg-primary/5',
          )}
        />
      )}
      {error ? (
        <p role="alert" className="text-destructive text-xs font-normal">
          {error}
        </p>
      ) : null}
    </VariantDetailField>
  );
}

function MarketPricesHere({
  storeId,
  productId,
  variant,
}: {
  storeId: string;
  productId: string;
  variant: Variant;
}) {
  const { t } = useTranslation('storeCatalog');
  const markets = useMarkets(storeId);
  return (
    <VariantDetailGroup title={t('storeVariants.groups.markets')}>
      {markets.isPending ? (
        <Skeleton className="h-10" />
      ) : markets.error ? (
        <ErrorState error={markets.error} onRetry={() => void markets.refetch()} compact />
      ) : markets.data.length === 0 ? (
        <p className="text-muted-foreground text-[13px]">{t('marketPrices.noMarkets')}</p>
      ) : (
        <VariantDetailGrid>
          {markets.data.map((market) => (
            <MarketPriceField
              key={market.id}
              storeId={storeId}
              productId={productId}
              variant={variant}
              market={market}
            />
          ))}
        </VariantDetailGrid>
      )}
    </VariantDetailGroup>
  );
}

function SharedDetails({
  storeId,
  storeProductId,
  product,
  variant,
}: {
  storeId: string;
  storeProductId: string;
  product: Product;
  variant: Variant;
}) {
  const { t } = useTranslation(['storeCatalog', 'products']);
  return (
    <VariantDetailGroup
      className="border-primary/20 border-t pt-3.5 sm:col-span-2"
      title={
        <span className="flex flex-wrap items-center gap-2">
          {t('storeCatalog:storeVariants.groups.shared')}
          <ScopeChip scope="shared" />
          <span className="text-muted-foreground text-xs font-normal">
            {t('storeCatalog:storeVariants.sharedHint')}
          </span>
        </span>
      }
      action={
        <Link
          to="/s/$storeId/catalog/$storeProductId/variants/$variantId"
          params={{ storeId, storeProductId, variantId: variant.id }}
          className="text-primary text-[13px] whitespace-nowrap hover:underline"
          aria-label={t('products:variantsCard.openVariantFor', { variant: variant.title })}
        >
          {t('products:variantsCard.openVariant')}
        </Link>
      }
    >
      <div className="grid gap-x-6 gap-y-[1.125rem] sm:grid-cols-2">
        <VariantFieldGroups product={product} variant={variant} shared />
      </div>
    </VariantDetailGroup>
  );
}

export interface StoreVariantDetailsProps {
  storeId: string;
  storeProductId: string;
  product: Product;
  variant: Variant;
}

/**
 * A store variant's View more: this store's pricing, stock and market prices, then the shared
 * details, which edit the master and so reach every store.
 */
export function StoreVariantDetails({
  storeId,
  storeProductId,
  product,
  variant,
}: StoreVariantDetailsProps) {
  return (
    <div className="grid gap-x-6 gap-y-[1.125rem] sm:grid-cols-2">
      <PricingHere variant={variant} />
      <StockHere storeId={storeId} variant={variant} />
      <MarketPricesHere storeId={storeId} productId={product.id} variant={variant} />
      <SharedDetails
        storeId={storeId}
        storeProductId={storeProductId}
        product={product}
        variant={variant}
      />
    </div>
  );
}
